import { type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Scale, AlertTriangle, Banknote, ShieldCheck, ShieldAlert } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import {
  fetchLedgerBalances,
  fetchOpenIncidents,
  fetchRecentPayments,
  formatFcfa,
  type LedgerBalance,
} from "@/lib/djayma";

// ---------------------------------------------------------------------------
// DJAYMA Finance reconciliation (Epic 5.2) — the finance-scoped view over the
// money engine: per-account trial balance (Σ = 0), open Andon incidents (cash
// variance) awaiting an unblock, and the recent payment ledger. All reads are
// finance-scoped by RLS (migration 138); a non-finance JWT simply sees nothing.
// ---------------------------------------------------------------------------

const ACCOUNT_TYPE_LABELS: Record<LedgerBalance["account_type"], string> = {
  asset: "Actif",
  liability: "Passif",
  revenue: "Produit",
  expense: "Charge",
  clearing: "Compensation",
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  wave: "Wave",
  orange_money: "Orange Money",
  free_money: "Free Money",
  cash: "Espèces",
  bank_transfer: "Virement",
  credit: "Crédit",
};

function Kpi({
  icon,
  label,
  value,
  tone,
}: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly value: string | number;
  readonly tone: string;
}) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tone}`}>{icon}</div>
        <div>
          <p className="text-2xl font-semibold text-stone-900">{value}</p>
          <p className="text-sm text-stone-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

export default function FinanceReconciliation() {
  const balances = useQuery({ queryKey: ["djayma", "ledger-balances"], queryFn: fetchLedgerBalances, retry: false });
  const incidents = useQuery({ queryKey: ["djayma", "open-incidents"], queryFn: fetchOpenIncidents, retry: false });
  const payments = useQuery({ queryKey: ["djayma", "recent-payments"], queryFn: fetchRecentPayments, retry: false });

  const unreachable = balances.isError && incidents.isError && payments.isError;

  const rows = balances.data ?? [];
  // Trial balance must net to zero — the headline correctness check.
  const netBalance = rows.reduce((acc, r) => acc + Number(r.balance ?? 0), 0);
  const squared = Math.round(netBalance) === 0;
  const totalDebit = rows.reduce((acc, r) => acc + Number(r.debit_total ?? 0), 0);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <Scale className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">Réconciliation financière</h1>
            <p className="text-sm text-stone-500">
              Grand livre en partie double — balance, écarts de caisse, encaissements
            </p>
          </div>
        </div>

        {unreachable && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            Grand livre non joignable — réservé aux rôles finance (RLS). Vérifiez les migrations 131-138 et l&apos;exposition
            du schéma <code className="mx-1 rounded bg-amber-100 px-1">djayma</code>.
          </div>
        )}

        {/* KPI row */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi
            icon={squared ? <ShieldCheck className="h-5 w-5" /> : <ShieldAlert className="h-5 w-5" />}
            tone={squared ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}
            label={squared ? "Grand livre équilibré" : "Grand livre déséquilibré"}
            value={squared ? "✓" : formatFcfa(netBalance)}
          />
          <Kpi icon={<Banknote className="h-5 w-5" />} tone="bg-stone-100 text-stone-700"
               label="Mouvements (débit total)" value={formatFcfa(totalDebit)} />
          <Kpi icon={<AlertTriangle className="h-5 w-5" />} tone="bg-amber-100 text-amber-700"
               label="Incidents ouverts" value={incidents.data?.length ?? 0} />
          <Kpi icon={<Banknote className="h-5 w-5" />} tone="bg-emerald-100 text-emerald-700"
               label="Paiements récents" value={payments.data?.length ?? 0} />
        </div>

        {/* Open incidents (Andon) */}
        <section className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold text-stone-700">Écarts de caisse à traiter (Andon)</h2>
          {(incidents.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-stone-400">Aucun écart ouvert. ✅</p>
          ) : (
            <ul className="divide-y divide-stone-100 text-sm">
              {incidents.data?.map((i) => (
                <li key={i.id} className="flex items-center justify-between py-2">
                  <span className="text-stone-700">
                    {i.zone ?? "—"} · {i.vehicle_ref ?? "—"}
                    {i.detail && typeof i.detail.variance !== "undefined" && (
                      <span className="ml-2 text-rose-600">
                        écart {formatFcfa(Number(i.detail.variance))}
                      </span>
                    )}
                  </span>
                  <span className="text-stone-400">{i.route_date ?? ""}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Trial balance */}
        <section className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold text-stone-700">Balance des comptes</h2>
          {rows.length === 0 ? (
            <p className="text-sm text-stone-400">Aucune écriture.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-stone-400">
                  <tr>
                    <th className="py-2 pr-4 font-medium">Compte</th>
                    <th className="py-2 pr-4 font-medium">Type</th>
                    <th className="py-2 pr-4 font-medium text-right">Débit</th>
                    <th className="py-2 pr-4 font-medium text-right">Crédit</th>
                    <th className="py-2 pr-4 font-medium text-right">Solde</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {rows.map((r) => (
                    <tr key={r.account_id}>
                      <td className="py-2 pr-4 text-stone-700">{r.name}</td>
                      <td className="py-2 pr-4 text-stone-500">{ACCOUNT_TYPE_LABELS[r.account_type] ?? r.account_type}</td>
                      <td className="py-2 pr-4 text-right text-stone-500">{formatFcfa(r.debit_total)}</td>
                      <td className="py-2 pr-4 text-right text-stone-500">{formatFcfa(r.credit_total)}</td>
                      <td className={`py-2 pr-4 text-right font-medium ${Number(r.balance) < 0 ? "text-rose-600" : "text-stone-800"}`}>
                        {formatFcfa(r.balance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-stone-200">
                    <td className="py-2 pr-4 font-semibold text-stone-700" colSpan={4}>Net (doit être 0)</td>
                    <td className={`py-2 pr-4 text-right font-semibold ${squared ? "text-emerald-700" : "text-rose-600"}`}>
                      {formatFcfa(netBalance)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </section>

        {/* Recent payments */}
        <section className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold text-stone-700">Encaissements récents</h2>
          {(payments.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-stone-400">Aucun encaissement.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-stone-400">
                  <tr>
                    <th className="py-2 pr-4 font-medium">Méthode</th>
                    <th className="py-2 pr-4 font-medium">Statut</th>
                    <th className="py-2 pr-4 font-medium">Signature</th>
                    <th className="py-2 pr-4 font-medium text-right">Montant</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {payments.data?.map((p) => (
                    <tr key={p.id}>
                      <td className="py-2 pr-4 text-stone-700">{PAYMENT_METHOD_LABELS[p.method] ?? p.method}</td>
                      <td className="py-2 pr-4 text-stone-500">{p.status}</td>
                      <td className="py-2 pr-4">
                        {p.method === "cash" ? (
                          <span className="text-stone-400">—</span>
                        ) : p.webhook_signature_ok ? (
                          <span className="text-emerald-600">vérifiée</span>
                        ) : (
                          <span className="text-rose-600">non vérifiée</span>
                        )}
                      </td>
                      <td className="py-2 pr-4 text-right font-medium text-stone-800">{formatFcfa(p.amount_xof)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}
